import * as React from 'react';

import { Button, ConsoleDialog } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideCheck } from 'lucide-react';

import { itemGroupCreateAtom } from '../../../_graphql/mutationItemGroupCreate';
import {
  $canSubmitItemGroupForm,
  $itemGroupForm,
  $resetItemGroupForm,
} from '../../../_state/itemGroupForm.state';
import { useTranslations } from '../../../i18n/useTranslations';

import { ItemGroupFormFields } from './ItemGroupFormFields';

interface ItemGroupCreateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const ItemGroupCreateDialog = ({
  isOpen,
  onClose,
  onSuccess,
}: ItemGroupCreateDialogProps) => {
  const t = useTranslations();

  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const form = useAtomValue($itemGroupForm);
  const canSubmit = useAtomValue($canSubmitItemGroupForm);

  const resetForm = useSetAtom($resetItemGroupForm);
  const createItemGroup = useSetAtom(itemGroupCreateAtom({}));

  const toast = useToast();

  React.useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  const handleClose = React.useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleSubmit = React.useCallback(async () => {
    try {
      setIsSubmitting(true);
      await createItemGroup({
        name: form.name.trim(),
        number: form.number.trim(),
        parentId: form.parentId ?? null,
        description: form.description.trim(),
        notes: form.notes.trim(),
        defaultPricingUnit: form.defaultPricingUnit,
      });
      toast.success(t.inventoryView.itemGroupCreateDialog.messages.success);
      onSuccess?.();
      handleClose();
    } catch (err) {
      toast.error(
        t.inventoryView.itemGroupCreateDialog.messages.error(
          err instanceof Error ? err.message : 'Unknown error',
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [createItemGroup, form, toast, onSuccess, handleClose, t]);

  return (
    <ConsoleDialog
      className="@ItemGroupCreateDialog"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      title={t.inventoryView.itemGroupCreateDialog.title}
      content={<ItemGroupFormFields disabled={isSubmitting} />}
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            color="neutral"
            onPress={handleClose}
            isDisabled={isSubmitting}
          >
            {t.inventoryView.itemGroupCreateDialog.actions.cancel}
          </Button>
          <Button
            icon={<LucideCheck />}
            color="primary"
            onPress={handleSubmit}
            isDisabled={!canSubmit || isSubmitting}
          >
            {isSubmitting
              ? t.inventoryView.itemGroupCreateDialog.actions.creating
              : t.inventoryView.itemGroupCreateDialog.actions.create}
          </Button>
        </div>
      }
    />
  );
};
